/**
 * lib/format.ts
 * Small text transforms shared by every screen. Kept apart from lib/status.ts,
 * which maps DOMAIN values; this file only reshapes arbitrary strings.
 */

/** `petty_cash` → `petty cash`. Option-list values are stored lower_snake. */
export const humanize = (value: string | null | undefined): string =>
  value ? value.replace(/_/g, ' ').trim() : '';

/** `petty cash` → `Petty cash`. Sentence case, not Title Case — see the design. */
export const sentenceCase = (value: string | null | undefined): string => {
  const v = humanize(value);
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : '';
};

/**
 * The `type · ref` meta line under a holder name, and every other dotted
 * meta line in the product. Empty parts are dropped so a missing reference
 * does not leave a dangling separator.
 */
export const dotted = (...parts: (string | null | undefined | false)[]): string =>
  parts.filter((p): p is string => typeof p === 'string' && p.trim().length > 0).join(' · ');

/** "4 assignments" / "1 assignment" — pluralisation with the count in front. */
export const countLabel = (n: number, singular: string, plural = `${singular}s`): string =>
  `${n} ${n === 1 ? singular : plural}`;

/**
 * Slug a user-typed option value the same way the API does, so the chip the
 * user sees immediately after adding matches the value that comes back on the
 * next fetch. Currencies are uppercased and capped at 4 characters; approver
 * names are kept exactly as typed (they are people, not enums).
 */
export function slugOption(listKind: string, raw: string): string {
  const value = raw.trim();
  if (listKind === 'currencies') return value.toUpperCase().slice(0, 4);
  if (listKind === 'approvers') return value;
  return value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

/** Truncate for a single-line cell, with a real ellipsis rather than "...". */
export const ellipsize = (value: string, max: number): string =>
  value.length <= max ? value : `${value.slice(0, Math.max(0, max - 1)).trimEnd()}…`;

/** Case-insensitive "does this row match the search box" test. */
export const matchesQuery = (query: string, ...fields: (string | null | undefined)[]): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(q));
};
